import { Box, Chip, CircularProgress, Fade } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useState } from "react"

import { CompanyPreferenceSection } from "./CompanyPreferenceSection"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import { PermissionActionType } from "@/constants/permissions"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { EditSiblingCompaniesDialog } from "@/screens/Companies/edit/components/preferences/components/sibling-companies/EditSiblingCompaniesDialog"
import { useGetCompanySiblings } from "@/screens/Companies/edit/components/preferences/hooks/useGetCompanySiblings"

interface SiblingCompaniesProps {
  companyId: string
}

export const SiblingCompanies: FC<SiblingCompaniesProps> = ({ companyId }) => {
  const { siblingCompanies, isFetching, hasError, refetch } = useGetCompanySiblings({ companyId })
  const { hasPermissions } = usePermissionManager()
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false)

  const canEdit = hasPermissions([PermissionActionType.CUSTOMER_MAINTENANCE])

  const handleOpenDialog = () => {
    setIsEditOpen(true)
  }

  const handleCloseDialog = (shouldRefetch?: boolean) => {
    setIsEditOpen(false)
    if (shouldRefetch) {
      refetch()
    }
  }

  if (isFetching && !siblingCompanies) return <CircularProgress />

  if (hasError) return <InformationMissingAlert title='Failed to load sibling companies' />

  if (!siblingCompanies?.length) {
    return (
      <>
        <Fade in unmountOnExit timeout={500}>
          <InformationMissingAlert
            title={canEdit ? "Start by selecting sibling companies" : "No sibling companies have been added"}
            action={
              canEdit && (
                <Button
                  color='inherit'
                  size='small'
                  appearance='outlined'
                  className='text-primary'
                  startIcon={<AddOutlined />}
                  onClick={handleOpenDialog}
                  data-testid='company.sibling-companies.add-button'
                >
                  Add Sibling Company
                </Button>
              )
            }
          />
        </Fade>
        {isEditOpen && <EditSiblingCompaniesDialog onClose={handleCloseDialog} companyId={companyId} />}
      </>
    )
  }

  return (
    <>
      <CompanyPreferenceSection
        title='Sibling Companies'
        onEdit={handleOpenDialog}
        canEdit={canEdit}
        editButtonTestId='company.sibling-companies.edit-button'
        isLoading={isFetching}
      >
        <Box className='flex flex-col'>
          <Box className='flex flex-wrap items-center gap-2'>
            {siblingCompanies.map((sibling) => (
              <Chip key={sibling.companyId} label={sibling.name} className='rounded-sm bg-primary-100' />
            ))}
          </Box>
        </Box>
      </CompanyPreferenceSection>
      {isEditOpen && (
        <EditSiblingCompaniesDialog
          onClose={handleCloseDialog}
          companyId={companyId}
          initialValues={{
            companies: siblingCompanies,
          }}
        />
      )}
    </>
  )
}
